import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import { ScrollReveal } from '@/components/ui/scroll-reveal';

// ═══════════════════════════════════════════════════════════
// LexiqueContent — Océanne (single source of truth).
// Réutilisé par LexiqueModal (variant="modal") + routes/lexique.tsx (variant="page").
// 10 termes AMG démystifiés. Schema.org DefinedTermSet présent sur la page SEO.
// ═══════════════════════════════════════════════════════════

interface LexiqueContentProps {
  variant: 'modal' | 'page';
  onClose?: () => void;
}

export function LexiqueContent({ variant, onClose }: LexiqueContentProps) {
  const { lang } = useLang();
  const text = ta(t.lexique, lang);
  const isPage = variant === 'page';

  const terms = [
    { id: 'amg', term: text.term_amg, def: text.def_amg },
    { id: 'capital', term: text.term_capital, def: text.def_capital },
    { id: 'survie', term: text.term_survie, def: text.def_survie },
    { id: 'preexist', term: text.term_preexist, def: text.def_preexist },
    { id: 'antecedents', term: text.term_antecedents, def: text.def_antecedents },
    { id: 'soumission', term: text.term_soumission, def: text.def_soumission },
    { id: 'souscription', term: text.term_souscription, def: text.def_souscription },
    { id: 'collectif', term: text.term_collectif, def: text.def_collectif },
    { id: 'loi25', term: text.term_loi25, def: text.def_loi25 },
    { id: 'amf', term: text.term_amf, def: text.def_amf }
  ];

  // Schema.org DefinedTermSet JSON-LD (page SEO seulement, crawlable)
  const schemaJsonLd = isPage
    ? {
        '@context': 'https://schema.org',
        '@type': 'DefinedTermSet',
        name: lang === 'fr' ? 'Lexique de l\'assurance maladie grave' : 'Critical illness insurance glossary',
        inDefinedTermSet: 'https://oceannebrodeur.com/lexique',
        hasDefinedTerm: terms.map((entry) => ({
          '@type': 'DefinedTerm',
          '@id': `#${entry.id}`,
          name: entry.term,
          description: entry.def
        }))
      }
    : null;

  return (
    <div>
      {/* Schema.org JSON-LD pour SEO (page seulement) */}
      {schemaJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd) }}
        />
      )}

      {/* Body : liste des 10 termes définis */}
      <div>
        {terms.map((entry, i) => (
          <ScrollReveal
            key={entry.id}
            as="article"
            delay={(Math.min((i % 5) * 100, 400)) as 0 | 100 | 200 | 300 | 400}
            {...{ id: entry.id }}
            className={`pb-8 ${i < terms.length - 1 ? 'mb-8 border-b border-border/40' : ''}`}
          >
            <h2 className="mb-3 font-display text-2xl text-balance text-foreground md:text-3xl">
              {entry.term}
            </h2>
            <p className="font-sans text-base leading-relaxed text-pretty hyphens-auto text-foreground/80">
              {entry.def}
            </p>
          </ScrollReveal>
        ))}
      </div>

      {/* CTA "Lire version complète" → page SEO (UNIQUEMENT en modal) */}
      {!isPage && (
        <div className="mt-10 border-t border-border/60 pt-8">
          <Link
            to="/lexique"
            onClick={onClose}
            className="group inline-flex items-center gap-2 font-sans text-sm font-bold uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
          >
            {text.modal_cta_full}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      )}

      {/* Footer crossover (page SEO seulement) — lien vers Carnet + Colophon */}
      {isPage && (
        <div className="mt-12 border-t border-border/60 pt-8 flex flex-wrap items-center justify-between gap-4 text-sm font-sans">
          <Link to="/" className="text-foreground/70 hover:text-primary transition-colors">
            {lang === 'fr' ? '← Retour à l\'accueil' : '← Back to home'}
          </Link>
          <div className="flex gap-6">
            <Link to="/carnet" className="text-foreground/70 hover:text-primary transition-colors">
              {lang === 'fr' ? 'Carnet de ressources →' : 'Resource book →'}
            </Link>
            <Link to="/colophon" className="text-foreground/70 hover:text-primary transition-colors">
              {lang === 'fr' ? 'Colophon →' : 'Colophon →'}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
